import { red } from '@mui/material/colors';
import React, { useContext } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { get_cod } from '../../../utils/cod';
import { displayLocation } from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';
import { OrderIdWithPopover } from '../../common/order-id-with-popover/order-id-with-popover';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import {
  getDeliveryStateColor,
  getDeliveryStateIcon,
  showDeliveryState,
} from '../../common/styles/order-states.style';
import { showPickupTaskChip } from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { showDeliveryTaskState } from '../../common/styles/delivery-task.styles';
import CachedIcon from '@mui/icons-material/Cached';

import OrderNotes from '../../common/order-notes/order-notes';
import { display_phone_number } from '../../../utils/phone_numbers';
import CustomerWithNavigate from '../../common/customer-with-navigate/customer-with-navigate';
import { format, formatDate } from 'date-fns';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { UserType } from '../../../enums/users';
import { Button } from '@mui/material';
import IncompleteAddressError from '../incomplete-address-error/incomplete-address-error';
import { QuickFilterScope } from '../../../enums/quick-filters';
import { IQuickFilter } from '../../../interfaces/common/quick-filters';

export const relevant_fields = [
  'id',
  'order_id',
  'reference_id',
  'creation_date',
  'merchant',
  'customer',
  'price',
  'delivery_fee',
  'workflow',
  'number_of_packages',
  'customer_location',
  'merchant_location',
  'is_exchange',
  'delivery_state',
  'tracking_hash',
  'description',
  'private_note',
  'task_notes',
  'public_note',
  'frozen_until',
  'frozen_because',
  'return_reason',
  'exchange_for',
  'labels',
  'source',
  'updated_by_us',
];

interface ResponsiveOrdersListingProps {
  ordersList: OrderType[];
  selectedOrders: Map<string, OrderType>;
  updateSelectedOrders: Updater<Map<string, OrderType>>;
  isLoading: boolean;
  sorting: SortingByColumnState;
  setSorting: React.Dispatch<React.SetStateAction<SortingByColumnState>>;
  applyQuickFilter: (quickFilter: IQuickFilter | null) => void;
  sourceView: string;
}

const ResponsiveOrdersListing = ({
  ordersList,
  selectedOrders,
  updateSelectedOrders,
  isLoading,
  sorting,
  setSorting,
  applyQuickFilter,
  sourceView
}: ResponsiveOrdersListingProps) => {
  const { user } = useContext(AuthContext);
  return (
    <GenericList<OrderType>
      quickFiltersScope={QuickFilterScope.ORDER}
      items={ordersList}
      selectionEnabled
      selectedItems={selectedOrders}
      updateSelectedItems={updateSelectedOrders}
      isLoading={isLoading}
      fullWidth
      applyQuickFilter={applyQuickFilter}
      sourceView={sourceView}
      columns={[
        {
          header: (
            <>
              <b>
                Order Id <br /> (Ref #)
              </b>
            </>
          ),
          id: 'order_id',
          display_body: (order) => (
            <OrderIdWithPopover order={order} />
          ),
        },
        {
          header: (
            <div
              onClick={() =>
                setSorting({
                  ...sorting,
                  order:
                    sorting.order === 'increasing'
                      ? 'decreasing'
                      : 'increasing',
                })
              }
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <b>
                Creation Date
                {sorting.order === 'increasing' ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </b>
            </div>
          ),
          display_body: (order) => (
            <>
              {order.creation_date == undefined
                ? 'Unknown'
                : format(order.creation_date!, 'dd-MM-yyyy HH:mm')}
            </>
          ),
          id: 'creation_date',
        },
        {
          header: <b>Merchant</b>,
          id: 'merchant',
          display_body: (order) => (
            <a href={`/merchant/${order.merchant?.id}`}>
              <b>{order.merchant?.name}</b>
            </a>
          ),
          hidden: user?.type == UserType.MERCHANT,
        },
        {
          header: <b>Customer</b>,
          id: 'customer',
          display_body: (order) => (
            <>
              <CustomerWithNavigate customer={order.customer!} />
            </>
          ),
        },
        {
          header: <b>Workflow</b>,
          id: 'workflow',
          display_body: (order) =>
            showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason),
        },
        {
          header: <b>COD</b>,
          id: 'cod',
          display_body: (order) => displayMoney(get_cod(order)),
        },
        {
          header: <b>POP</b>,
          id: 'pop',
          display_body: (order) => displayMoney(order.pop),
        },
        {
          header: <b>Reason</b>,
          id: 'reason',
          display_body: (order) => order.frozen_because ?? '',
        },
        {
          header: <b>Notes</b>,
          id: 'notes',
          display_body: (order) => <>
            {!order.customer_location?.area && <IncompleteAddressError /> }
            <OrderNotes order={order} />
          </>,
        },
        {
          header: <b>Status</b>,
          id: 'status',
          display_body: (order) => showDeliveryState(order.delivery_state),
        },
      ]}
      getItemUID={(order) => order.id}
      rowConfigs={[
        {
          display_row: (order) => <b>Order #{order.order_id}</b>,
        },
        {
          display_row: (order) =>
            mobileDoubleItemsGrid([
              <div key="date">
                {order.creation_date &&
                  new Date(order.creation_date).toLocaleDateString()}
              </div>,
              showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason),
            ]),
        },
        {
          display_row: (order) => (
            <IconWithText>
              <StorefrontIcon color="primary" />{' '}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/merchant/${order.merchant?.id}`);
                }}
              >
                <b>
                  {order.merchant?.name} {order.merchant?.phone_number}
                </b>
              </div>
            </IconWithText>
          ),
        },
        {
          display_row: (order) => (
            <IconWithText>
              <PersonOutlineIcon color="primary" />{' '}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`/customer/${order.customer?.id}`);
                }}
              >
                <b>
                  {order.customer?.name} {order.customer?.phone_number}{' '}
                  {order.customer?.secondary_phone_number &&
                    `(${display_phone_number(order.customer.secondary_phone_number)})`}
                </b>
              </div>
            </IconWithText>
          ),
        },
        {
          display_row: (order) => {
            return mobileDoubleItemsGrid([
              <div key="cod">
                <b>COD: </b> {displayMoney(get_cod(order))}
              </div>,
              <div key="cod">
                <b>POP: </b> {displayMoney(order.pop)}
              </div>,
            ]);
          },
        },
        {
          display_row: (order) => <OrderNotes order={order} />,
        },
      ]}
      // cardFooter={(order) => { return { content: <>{order.delivery_state}</>, background: 'lightgrey' } }}
      cardIcon={(order) => <InventoryIcon color={'primary'} />}
      onCardClick={(order) => window.open(`/order/${order.id}`, '_blank')}
      cardFooter={(order) => {
        return {
          content: (
            <IconWithText centered>
              {getDeliveryStateIcon(order.delivery_state)}
              <>{order.delivery_state.replaceAll('_', ' ')}</>
            </IconWithText>
          ),
          background: getDeliveryStateColor(order.delivery_state),
        };
      }}
    />
  );
};

export default ResponsiveOrdersListing;
